import { C, type Hex, mix, Pix } from './pix';
import {
  handMadeArt,
  type IconArt,
  type IconKind,
  type IconSize,
  kindDef,
  type Marker,
  pal,
  type Pal,
  type Plate,
} from './store';

/**
 * Puts an icon together: its ground (tile or medallion), its picture (lit from the upper right and
 * outlined in a deep shade of its tint), then its kind mark. Pure: the result is a pixel buffer.
 */

/** The deep, slightly coloured ink every picture is outlined in. */
export const outlineOf = (tint: Hex): Hex => mix(tint, SHADOW, 0.86);

/** Shadows lean to a deep plum rather than black, so dark gold stays amber instead of olive. */
export const SHADOW = '#24142c';

/** A bevelled tile: dark tint, lit along its top and right edge, shaded along its bottom and left. */
function squarePlate(o: Pix, t: Hex): void {
  const S = o.w;
  const edge = mix(t, SHADOW, 0.9);
  o.rect(0, 0, S, S, edge);
  o.rect(1, 1, S - 2, S - 2, mix(t, SHADOW, 0.72));
  // A soft pool of the tint behind the picture, as one flat tone.
  o.ell(S / 2, S / 2, S * 0.38, S * 0.38, mix(t, SHADOW, 0.63));
  const lit = mix(t, SHADOW, 0.25);
  const shade = mix(t, SHADOW, 0.84);
  o.rect(2, 1, S - 3, 1, lit);
  o.rect(S - 2, 1, 1, S - 3, lit);
  o.rect(1, S - 2, S - 3, 1, shade);
  o.rect(1, 2, 1, S - 4, shade);
  // Rounded corners.
  for (const [x, y] of [
    [0, 0],
    [S - 1, 0],
    [0, S - 1],
    [S - 1, S - 1],
  ] as const)
    o.erase(x, y);
  o.px(1, 1, edge)
    .px(S - 2, 1, edge)
    .px(1, S - 2, edge)
    .px(S - 2, S - 2, edge);
}

/** A round medallion, lit the same way. */
function roundPlate(o: Pix, t: Hex): void {
  const S = o.w;
  const c = S / 2;
  o.ell(c, c, c, c, mix(t, SHADOW, 0.9));
  o.ell(c, c, c - 1, c - 1, mix(t, SHADOW, 0.68));
  o.ring(c - 0.5, c - 0.5, c - 1.5, c - 1.5, mix(t, SHADOW, 0.25), -2.6, 0.5);
  o.ring(c - 0.5, c - 0.5, c - 1.5, c - 1.5, mix(t, SHADOW, 0.84), 1.1, 3.6);
}

export function plate(o: Pix, shape: Plate, t: Hex): void {
  if (shape === 'square') squarePlate(o, t);
  else if (shape === 'round') roundPlate(o, t);
}

/** The pixel origin and size of the 破 seal for an icon size. */
export const breakSeal = (S: number) =>
  S >= 32 ? { x: S - 12, y: S - 12, s: 12 } : { x: S - 7, y: S - 7, s: 7 };
/** The top-left tab of 療 and 守. */
export const tabSize = (S: number) => (S >= 32 ? 10 : 7);

/** Draws a kind mark over a finished icon. */
export function mark(o: Pix, m: Marker): void {
  const S = o.w;
  const big = S >= 32;
  if (m === 'break') {
    const { x, y, s } = breakSeal(S);
    o.rect(x, y, s, s, C.sealDark);
    o.rect(x + 1, y + 1, s - 2, s - 2, C.seal);
    const at = (pts: [number, number][]) => pts.map(([a, b]) => [x + 1 + a, y + 1 + b] as [number, number]);
    if (big) {
      // A jagged paper crack across the seal, with a splinter off it.
      o.path(
        at([
          [8, 0],
          [5, 3],
          [7, 4],
          [3, 7],
          [4, 8],
          [1, 9],
        ]),
        C.paper,
      );
      o.path(
        at([
          [5, 3],
          [2, 3],
        ]),
        C.paper,
      );
      o.path(
        at([
          [7, 4],
          [9, 6],
        ]),
        C.paper,
      );
    } else
      for (const [a, b] of at([
        [3, 0],
        [2, 1],
        [3, 2],
        [2, 3],
        [1, 4],
      ]))
        o.px(a, b, C.paper);
  } else if (m === 'power') {
    // A seal's frame: two pixels of red with a paper line inside (one of each when small).
    const ring = (k: number, c: Hex) => {
      o.rect(k, k, S - 2 * k, 1, c);
      o.rect(k, S - 1 - k, S - 2 * k, 1, c);
      o.rect(k, k, 1, S - 2 * k, c);
      o.rect(S - 1 - k, k, 1, S - 2 * k, c);
    };
    ring(0, C.seal);
    if (big) {
      ring(1, C.seal);
      ring(2, C.paper);
      // Dark nicks at the corners, like a carved seal's border.
      for (const [x, y] of [
        [0, 0],
        [S - 1, 0],
        [0, S - 1],
        [S - 1, S - 1],
      ] as const)
        o.px(x, y, C.sealDark);
    } else ring(1, C.paper);
  } else if (m === 'heal' || m === 'buff' || m === 'guard') {
    const t = tabSize(S);
    const ink = m === 'guard' ? '#1a2434' : '#14281e';
    o.rect(0, 0, t, t, ink);
    o.rect(1, 1, t - 2, t - 2, C.paper);
    o.erase(0, 0);
    const col = m === 'guard' ? '#3a6aa8' : C.jadeDark;
    if (m === 'heal') {
      if (big) o.rect(4, 2, 2, 6, col).rect(2, 4, 6, 2, col);
      else o.rect(3, 2, 1, 3, col).rect(2, 3, 3, 1, col);
    } else if (m === 'buff') {
      if (big) o.rect(4, 2, 2, 1, col).rect(3, 3, 4, 1, col).rect(2, 4, 6, 1, col).rect(4, 5, 2, 3, col);
      else o.px(3, 1, col).rect(2, 2, 3, 1, col).rect(3, 3, 1, 3, col);
    } else if (big)
      o.poly(
        [
          [2, 2],
          [7, 2],
          [7, 5],
          [4.5, 8],
          [2, 5],
        ],
        col,
      ).rect(4, 3, 1, 3, '#a8c8f0');
    else o.rect(2, 2, 3, 2, col).px(3, 4, col);
  } else if (m === 'debuff') {
    o.rect(0, 0, S, 1, C.seal)
      .rect(0, S - 1, S, 1, C.seal)
      .rect(0, 0, 1, S, C.seal)
      .rect(S - 1, 0, 1, S, C.seal);
    o.erase(0, 0)
      .erase(S - 1, 0)
      .erase(0, S - 1)
      .erase(S - 1, S - 1);
  }
}

/** The picture layer at a size: drawn (or halved from 32), lit, outlined. */
function picture(art: IconArt, id: string, p: Pal, tint: Hex, size: IconSize): Pix {
  let layer: Pix;
  if (size === 16 && art.draw16) {
    layer = new Pix(16, 16);
    art.draw16(layer, { id, size: 16, p });
  } else {
    layer = new Pix(32, 32);
    art.draw(layer, { id, size: 32, p });
    if (size === 16) layer = layer.half();
  }
  if (!art.flat) layer.light();
  return layer.outline(outlineOf(tint));
}

function compose(kind: IconKind, id: string, size: IconSize): Pix {
  const def = kindDef(kind);
  if (!def) throw new Error(`Unknown icon kind: ${kind}`);
  const art = handMadeArt(kind, id) ?? def.fallback(id);
  const native = art.native ?? def.native ?? 32;
  if (native === 16) {
    const o = compose16(kind, id, art, def.plate, def.marker?.(id));
    return size === 32 ? o.double() : o;
  }
  const tint = art.tint ?? def.tint(id);
  const o = new Pix(size, size);
  plate(o, art.plate ?? def.plate, tint);
  o.blit(picture(art, id, pal(tint), tint, size));
  mark(o, art.marker ?? def.marker?.(id) ?? 'none');
  return o;
}

function compose16(kind: IconKind, id: string, art: IconArt, shape: Plate, m?: Marker): Pix {
  const tint = art.tint ?? kindDef(kind)!.tint(id);
  const p = pal(tint);
  const o = new Pix(16, 16);
  plate(o, art.plate ?? shape, tint);
  const layer = new Pix(16, 16);
  art.draw(layer, { id, size: 16, p });
  if (!art.flat) layer.light();
  o.blit(layer.outline(outlineOf(tint)));
  mark(o, art.marker ?? m ?? 'none');
  return o;
}

/** An icon's pixels (no canvas): hand-made where there is one, generated otherwise. */
export function iconPixels(kind: IconKind, id: string, size: IconSize = 32): Pix {
  return compose(kind, id, size);
}
